import { useEffect, useRef, useMemo, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage } from 'react-intl';
import { AGGClient } from 'src/clients/agg-client/agg-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { Tracking } from '@/analytics/atl-analytics/tracking';
import { useAnalyticsClient } from '@/analytics/wac-analytics/analytics-client';
import { Button } from '@/libraries/design/components/button/src/button';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';
import { useLoggedInUser } from '@/utils/logged-in-user-provider';

import { getIsSMBSalesLead } from './contact-sales-cta-helpers';

const fullWidthStyles = css({
	maxWidth: 'none',
	width: '100%',
});

export const useContactSalesCtaEligibility = (): {
	isEligible: boolean;
	isLoading: boolean;
} => {
	const { accountId } = useLoggedInUser();
	// feature gate has configuration to only be enabled on specified page paths
	// https://switcheroo.atlassian.com/ui/gates/c5b6c155-5937-4f7d-9a5f-154015312a23/key/wac_sales_leads_cta_global_nav
	const isFeatureGateEnabled = checkFeatureFlagGate(featureFlags.wacSalesLeadsCtaGlobalNav);

	const [isSalesLead, setIsSalesLead] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);

	useEffect(() => {
		if (!isFeatureGateEnabled || !accountId) return;
		setIsLoading(true);
		const aggClient = new AGGClient({
			gatewayUrl:
				process.env['NODE_ENV'] === 'production'
					? 'https://www.atlassian.com/gateway/api/graphql'
					: 'https://wac.stg.internal.atlassian.com/gateway/api/graphql',
		});

		getIsSMBSalesLead({
			accountId,
			aggClient,
		})
			.then((response) => {
				setIsSalesLead(!!response);
			})
			.catch(() => {
				console.error('Error fetching GUPS user profile for SMB Sales lead');
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [accountId, isFeatureGateEnabled]);

	if (!accountId || !isFeatureGateEnabled) {
		return { isEligible: false, isLoading: false };
	}

	return {
		isEligible: isSalesLead,
		isLoading,
	};
};

export const ContactSalesCta = ({ isFullWidth }: { isFullWidth?: boolean }) => {
	const { isEligible, isLoading } = useContactSalesCtaEligibility();
	const productKey = useMemo<ProductKeyTypes>(() => {
		const pathname = globalThis.location?.pathname ?? '';
		if (pathname.includes('/software/jira')) return 'jira';
		if (pathname.includes('/software/confluence')) return 'confluence';
		if (pathname.includes('/enterprise/contact')) return 'enterpriseContactSales';
		if (pathname === '/') return 'home';
		return undefined;
	}, []);

	if (isLoading || !isEligible) {
		return;
	}

	return <CtaButtonRenderer isFullWidth={isFullWidth} productKey={productKey} />;
};

const CtaButtonRenderer = ({
	isFullWidth,
	productKey,
}: {
	isFullWidth?: boolean;
	productKey: ProductKeyTypes;
}) => {
	const analyticsClient = useAnalyticsClient();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const localizedHref = useLocalizedHref('/enterprise/contact?formType=product-features');

	const hasFiredRef = useRef(false);

	useEffect(() => {
		if (analyticsClient && !hasFiredRef.current) {
			hasFiredRef.current = true;
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'viewed',
				actionSubject: 'button',
				actionSubjectId: 'contactSalesCta',
				source: 'globalNav',
				attributes: {
					isMobile: !!isFullWidth,
					productKey: productKey,
				},
			}).fire();
		}
	}, [analyticsClient, createAnalyticsEvent, isFullWidth]);

	useEffect(() => {
		Tracking.autoTrackLinks(true);
	}, []);

	return (
		<Button
			variant="primary_alt"
			css={isFullWidth && fullWidthStyles}
			href={localizedHref}
			dataEventProps={{
				'data-product-key': productKey,
				'data-event': 'clicked',
				'data-event-component': 'linkButton',
				'data-event-container': 'navbar',
				'data-label-english': 'Contact us',
			}}
		>
			<FormattedMessage id="global-nav.contact-sales-cta.label" defaultMessage="Contact us" />
		</Button>
	);
};
